import { useState } from "react";
import type { FileItem } from "../../types";
import { FileIcon } from "../shared/FileIcon";
import { cn } from "@/shared/lib/cn";
import { getThumbnailUrl, isPdf } from "../../utils/fileUtils";
import { Checkbox } from "@/shared/ui/checkbox";

interface GalleryItemProps {
  file: FileItem;
  useLargeThumbnail: boolean;
  isLargeGallery: boolean;
  isSelected: boolean;
  isMultiSelected?: boolean;
  onFileSelect: (file: FileItem) => void;
  onMultiSelect?: (file: FileItem, checked: boolean) => void;
  onImageClick?: (file: FileItem) => void;
}

export function GalleryItem({
  file,
  useLargeThumbnail,
  isLargeGallery,
  isSelected,
  isMultiSelected = false,
  onFileSelect,
  onMultiSelect,
  onImageClick,
}: GalleryItemProps) {
  const [imageError, setImageError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const thumbnailUrl = useLargeThumbnail
    ? file.thumbnailLargeUrl || file.thumbnailUrl
    : file.thumbnailSmallUrl || file.thumbnailUrl;
  const fullUrl = thumbnailUrl ? getThumbnailUrl(thumbnailUrl) : undefined;
  const isImage = file.type === "image";
  const isFilePdf = isPdf(file);
  const isViewable = isImage || isFilePdf;
  // Fit mode for thumbnails:
  // const imageFitClass = "object-contain"; // shows whole image, no cropping
  const imageFitClass = "object-cover"; // fills the frame (current setting)

  const handleItemClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.closest('[role="checkbox"]') ||
      target.closest('button[type="button"]')
    ) {
      return;
    }

    if (isSelected && isViewable && onImageClick) {
      if (
        isFilePdf ||
        target.tagName === "IMG" ||
        target.closest("img")
      ) {
        e.stopPropagation();
        onImageClick(file);
        return;
      }
    }

    onFileSelect(file);
  };

  const handleCheckboxChange = (checked: boolean) => {
    if (onMultiSelect) {
      onMultiSelect(file, checked);
    }
  };

  return (
    <div
      className={cn(
        "group relative flex h-full flex-col gap-2 rounded-xl border-2 border-transparent bg-white text-left transition-all hover:border-imperial-300 hover:shadow-md",
        isSelected && "border-imperial-500 shadow-md",
        isMultiSelected && "border-imperial-500 shadow-md",
        isLargeGallery ? "p-6" : "p-4"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {(isHovered || isMultiSelected) && onMultiSelect && (
        <div
          className="absolute top-2 left-2 z-10 inline-flex bg-white rounded shadow-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={isMultiSelected}
            onCheckedChange={handleCheckboxChange}
          />
        </div>
      )}
      <button
        onClick={handleItemClick}
        className="flex h-full flex-col gap-2 text-left"
      >
        <div
          className={cn(
            "flex w-full items-center justify-center overflow-hidden rounded-lg bg-grid-50 aspect-4/3",
            isViewable && isSelected && onImageClick && "cursor-pointer"
          )}
        >
          {fullUrl && !imageError ? (
            <img
              src={fullUrl}
              alt={file.name}
              className={`h-full w-full ${imageFitClass}`}
              onError={() => setImageError(true)}
            />
          ) : (
            <FileIcon
              type={file.type}
              className={cn(
                "text-ink-300",
                isLargeGallery ? "h-16 w-16" : "h-12 w-12"
              )}
            />
          )}
        </div>
        <div className="w-full space-y-1">
          <p
            className={cn(
              "line-clamp-2 font-medium text-ink-900 group-hover:text-imperial-600",
              isLargeGallery ? "text-sm" : "text-xs"
            )}
          >
            {`${file.name}${file.extension}`}
          </p>
          <p
            className={cn(
              "text-ink-500",
              isLargeGallery ? "text-sm" : "text-xs"
            )}
          >
            {file.size}
          </p>
        </div>
      </button>
    </div>
  );
}
